/**
 * Global Shortcuts Store (Zustand)
 *
 * Manages global state for modal shortcuts accessible from anywhere in the app.
 * - QuickStatsModal (player/team search)
 * - AnalysisConfigPanel (AI config settings)
 * - GameModal (unified schedule + box score + lineup)
 * - Chart modals (when player/team selected from search)
 */

import type { LeagueKey } from '@parlay-picker/shared/league';
import type { ChartClickData } from '@views/picks/components/ParlayCard';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

// =============================================================================
// Game Modal Types
// =============================================================================

export type GameModalTab = 'boxScore' | 'lineup';

export interface GameModalState {
  /** null = schedule/picker mode (header opened); object = specific game selected */
  game: null | {
    gameId: string;
    homeTeam?: string;
    awayTeam?: string;
    leagueKey: LeagueKey;
    gameStartsAt?: string;
  };
  defaultTab: GameModalTab;
  highlightedPlayers: string[];
  /** For lineup tab: which team to show */
  teamAbbr?: string;
  opponentAbbr?: string;
}

// =============================================================================
// Store Types
// =============================================================================

interface GlobalShortcutsState {
  // Modal open/close state
  isQuickStatsOpen: boolean;
  isConfigPanelOpen: boolean;

  // Picks config fullscreen overlay (hides builder panel when open)
  isPicksConfigFullscreen: boolean;

  // Chart modal state (for when player/team selected from search)
  chartModal: ChartClickData | null;

  // Unified game modal state (schedule + box score + lineup)
  gameModal: GameModalState | null;

  // Actions
  openQuickStats: () => void;
  closeQuickStats: () => void;
  toggleQuickStats: () => void;

  openConfigPanel: () => void;
  closeConfigPanel: () => void;
  toggleConfigPanel: () => void;

  setPicksConfigFullscreen: (open: boolean) => void;

  setChartModal: (data: ChartClickData | null) => void;

  // Unified game modal actions
  openGameModal: (
    tab: GameModalTab,
    game?: GameModalState['game'],
    highlightedPlayers?: string[],
    teamAbbr?: string,
    opponentAbbr?: string,
  ) => void;
  closeGameModal: () => void;

  // Backward-compat aliases
  openBoxScore: (
    gameId: string,
    highlightedPlayers: string[],
    leagueKey: LeagueKey,
    context?: { homeTeam?: string; awayTeam?: string; gameStartsAt?: string },
  ) => void;
  closeBoxScore: () => void;
  openRosterIntel: () => void;
  closeRosterIntel: () => void;

  closeAllModals: () => void;
}

export const useGlobalShortcutsStore = create<GlobalShortcutsState>((set) => ({
  // Initial state
  isQuickStatsOpen: false,
  isConfigPanelOpen: false,
  isPicksConfigFullscreen: false,
  chartModal: null,
  gameModal: null,

  // Quick Stats actions
  openQuickStats: () => set({ isQuickStatsOpen: true }),
  closeQuickStats: () => set({ isQuickStatsOpen: false }),
  toggleQuickStats: () => set((s) => ({ isQuickStatsOpen: !s.isQuickStatsOpen })),

  // Config Panel actions
  openConfigPanel: () => set({ isConfigPanelOpen: true }),
  closeConfigPanel: () => set({ isConfigPanelOpen: false }),
  toggleConfigPanel: () => set((s) => ({ isConfigPanelOpen: !s.isConfigPanelOpen })),

  // Picks config fullscreen
  setPicksConfigFullscreen: (open) => set({ isPicksConfigFullscreen: open }),

  // Chart modal actions
  setChartModal: (data) => set({ chartModal: data }),

  // Unified game modal actions
  openGameModal: (tab, game = null, highlightedPlayers = [], teamAbbr, opponentAbbr) =>
    set({
      gameModal: {
        game,
        defaultTab: tab,
        highlightedPlayers,
        teamAbbr,
        opponentAbbr,
      },
    }),
  closeGameModal: () => set({ gameModal: null }),

  // Backward-compat: openBoxScore -> openGameModal('boxScore', ...)
  openBoxScore: (gameId, highlightedPlayers, leagueKey, context) =>
    set({
      gameModal: {
        game: gameId
          ? {
              gameId,
              leagueKey,
              homeTeam: context?.homeTeam,
              awayTeam: context?.awayTeam,
              gameStartsAt: context?.gameStartsAt,
            }
          : null,
        defaultTab: 'boxScore',
        highlightedPlayers,
      },
    }),
  closeBoxScore: () => set({ gameModal: null }),

  // Backward-compat: openRosterIntel -> openGameModal('lineup')
  openRosterIntel: () =>
    set({
      gameModal: {
        game: null,
        defaultTab: 'lineup',
        highlightedPlayers: [],
      },
    }),
  closeRosterIntel: () => set({ gameModal: null }),

  // Close all modals (useful for Escape key)
  closeAllModals: () =>
    set({
      isQuickStatsOpen: false,
      isConfigPanelOpen: false,
      chartModal: null,
      gameModal: null,
    }),
}));

// Selector hooks for common patterns
export const useIsQuickStatsOpen = (): boolean =>
  useGlobalShortcutsStore((s) => s.isQuickStatsOpen);
export const useIsConfigPanelOpen = (): boolean =>
  useGlobalShortcutsStore((s) => s.isConfigPanelOpen);
export const useGameModal = (): GameModalState | null =>
  useGlobalShortcutsStore((s) => s.gameModal);
export const useChartModal = (): ChartClickData | null =>
  useGlobalShortcutsStore((s) => s.chartModal);

// Backward-compat selectors
export const useIsRosterIntelOpen = (): boolean =>
  useGlobalShortcutsStore((s) => s.gameModal?.defaultTab === 'lineup' && s.gameModal !== null);
export const useBoxScoreModal = (): {
  gameId: string;
  highlightedPlayers: string[];
  leagueKey: LeagueKey;
  homeTeam?: string;
  awayTeam?: string;
  gameStartsAt?: string;
} | null =>
  // useShallow: the selector builds a fresh object, so without it zustand v5
  // would loop ("getSnapshot should be cached") on every render.
  useGlobalShortcutsStore(
    useShallow((s) => {
      if (!s.gameModal?.game) return null;
      return {
        gameId: s.gameModal.game.gameId,
        highlightedPlayers: s.gameModal.highlightedPlayers,
        leagueKey: s.gameModal.game.leagueKey,
        homeTeam: s.gameModal.game.homeTeam,
        awayTeam: s.gameModal.game.awayTeam,
        gameStartsAt: s.gameModal.game.gameStartsAt,
      };
    }),
  );
